/**
 * Favorites Management API Service
 * This service handles communication with the Azure Functions backend for favorites management.
 */

import axios from 'axios';

class FavoritesService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE || 'http://localhost:7071/api';
    // Ensure baseURL doesn't end with slash to avoid double slashes
    this.baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    console.log('FavoritesService initialized with baseURL:', this.baseURL);
  }

  /**
   * Get favorites for a user
   * @param {string} userId - User ID
   * @param {Object} filters - Optional filters
   * @returns {Promise} - API response
   */
  async getFavorites(userId, filters = {}) {
    try {
      console.log('Getting favorites for user:', userId, 'with filters:', filters);
      
      const params = { userId, ...filters };
      const response = await axios.get(`${this.baseURL}/favorites/get`, { params });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error getting favorites:', error);
      throw error;
    }
  }

  /**
   * Add a video to favorites
   * @param {string} userId - User ID (optional if included in favoriteData)
   * @param {Object} favoriteData - Favorite data
   * @returns {Promise} - API response
   */
  async addFavorite(userId, favoriteData) {
    try {
      // Support both single parameter (favoriteData with userId) and two parameters
      let actualUserId, actualData;
      
      if (typeof userId === 'object' && favoriteData === undefined) {
        // Single parameter case: addFavorite(favoriteData)
        actualData = userId;
        actualUserId = actualData.userId;
      } else {
        // Two parameter case: addFavorite(userId, favoriteData)
        actualUserId = userId;
        actualData = favoriteData;
      }
      
      console.log('Adding favorite:', actualData);
      
      const response = await axios.post(`${this.baseURL}/favorites/post`, {
        userId: actualUserId,
        itemId: actualData.itemId,
        itemType: actualData.itemType || 'blog-like', // Default to blog-like for blog favorites
        title: actualData.title,
        description: actualData.description,
        category: actualData.category,
        addedAt: new Date().toISOString(),
        ...actualData
      });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error adding favorite:', error);
      throw error;
    }
  }

  /**
   * Remove a video from favorites
   * @param {string} userId - User ID
   * @param {string} itemId - Item ID to remove
   * @param {string} itemType - Type of item (video, ebook, qna)
   * @returns {Promise} - API response
   */
  async removeFavorite(userId, itemId, itemType) {
    try {
      console.log('Removing favorite:', { userId, itemId, itemType });
      
      const response = await axios.delete(`${this.baseURL}/favorites/delete`, {
        data: { userId, itemId, itemType }
      });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error removing favorite:', error);
      throw error;
    }
  }

  /**
   * Update favorite metadata
   * @param {string} favoriteId - Favorite ID
   * @param {Object} updateData - Data to update
   * @returns {Promise} - API response
   */
  async updateFavorite(favoriteId, updateData) {
    try {
      console.log('Updating favorite:', favoriteId, 'with data:', updateData);
      
      const response = await axios.put(`${this.baseURL}/favorites/update`, {
        favoriteId,
        ...updateData
      });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error updating favorite:', error);
      throw error;
    }
  }

  /**
   * Clear all favorites for a user
   * @param {string} userId - User ID
   * @returns {Promise} - API response
   */
  async clearAllFavorites(userId) {
    try {
      console.log('Clearing all favorites for user:', userId);
      
      // Use correct backend endpoint with userId in query params
      const response = await axios.delete(`${this.baseURL}/favorites/all-delete?userId=${userId}`);
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error clearing all favorites:', error);
      throw error;
    }
  }

  /**
   * Check if item is in favorites
   * @param {string} userId - User ID
   * @param {string} itemId - Item ID
   * @param {string} itemType - Item type
   * @returns {Promise} - Boolean response
   */
  async isFavorite(userId, itemId, itemType) {
    try {
      const response = await this.getFavorites(userId, { type: itemType });
      const favoritesData = response.data || [];
      return favoritesData.some(fav => fav.itemId === itemId && fav.itemType === itemType);
    } catch (error) {
      console.error('Error checking favorite status:', error);
      return false;
    }
  }

  /**
   * Toggle favorite status with improved 409 handling
   * @param {string} userId - User ID
   * @param {Object} itemData - Item data
   * @returns {Promise} - New favorite status
   */
  async toggleFavorite(userId, itemData) {
    try {
      console.log('Toggling favorite for:', { userId, itemId: itemData.itemId, itemType: itemData.itemType });
      
      // First, check if item is already in favorites to avoid 409
      const existingFavorites = await this.getFavorites(userId, { type: itemData.itemType });
      const isCurrentlyFavorite = existingFavorites.data?.some(fav => 
        fav.itemId === itemData.itemId && fav.itemType === itemData.itemType
      ) || false;
      
      if (isCurrentlyFavorite) {
        // Item is already in favorites, remove it
        console.log('Item already in favorites, removing it');
        await this.removeFavorite(userId, itemData.itemId, itemData.itemType);
        return false; // Successfully removed
      } else {
        // Item is not in favorites, add it
        console.log('Item not in favorites, adding it');
        try {
          await this.addFavorite(userId, itemData);
          return true; // Successfully added
        } catch (addError) {
          // If we still get 409 (race condition), try to remove instead
          if (addError.response && addError.response.status === 409) {
            console.log('409 conflict detected during add, removing instead');
            await this.removeFavorite(userId, itemData.itemId, itemData.itemType);
            return false; // Successfully removed after conflict
          }
          throw addError; // Re-throw other errors
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      throw error;
    }
  }
}

const favoritesService = new FavoritesService();
export default favoritesService;
